/**
 * GovernanceScreen v2 — polished BP cards, styled badges, improved voter banner
 * Drop-in replacement for apps/mobile/src/screens/GovernanceScreen.tsx
 *
 * Changes vs v1:
 * - BpCard: gradient rank circle, styled Active/Standby badges, checkbox glow
 * - Voter banner: updated to banner-brand class with shield icon
 * - Tabs: pill-style with glow on active
 * - Standby info card: brand-light color scheme
 * - My Votes tab: 2×2 stat grid with card styling
 * - Confirm dialog: polished list with status badges
 * ALL business logic, API calls, store hooks, voting flow — UNCHANGED.
 */

import { useEffect, useState, useCallback, useMemo } from 'react'
import {
  getTopProducers,
  getVoterInfo,
  voteForProducers,
  countryFlag,
  type BlockProducer,
  type VoteInfo,
} from '@properwallet/chain-eos'
import type { TranslationKey } from '@properwallet/i18n'
import { useWalletStore } from '../store/wallet.store'
import { useTranslation } from '../hooks/useTranslation'
import BottomNav from '../components/BottomNav'
import AccountAvatar from '../components/AccountAvatar'

type Tab = 'top21' | 'standby' | 'myvote'

// ── BP Card v2 ─────────────────────────────────────────────────────────────────

interface BpCardProps {
  bp: BlockProducer
  rank: number
  checked: boolean
  selecting: boolean
  isCurrentVote: boolean
  onToggle: (account: string) => void
}

function BpCard({ bp, rank, checked, selecting, isCurrentVote, onToggle }: BpCardProps) {
  const flag = countryFlag(bp.countryCode)

  return (
    <button
      type="button"
      onClick={() => selecting && onToggle(bp.account)}
      className="w-full text-left transition-all"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderRadius: 14,
        background: checked
          ? 'linear-gradient(135deg, rgba(67,160,71,0.1), rgba(67,160,71,0.04))'
          : '#0c100c',
        border: `1px solid ${checked ? 'rgba(67,160,71,0.3)' : 'rgba(255,255,255,0.05)'}`,
        cursor: selecting ? 'pointer' : 'default',
        marginBottom: 8,
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      {/* Rank circle */}
      <div
        className="flex-shrink-0 flex items-center justify-center font-bold"
        style={{
          width: 32, height: 32, borderRadius: '50%',
          background: bp.isActive ? 'rgba(67,160,71,0.2)' : 'rgba(255,255,255,0.05)',
          border: `1px solid ${bp.isActive ? 'rgba(67,160,71,0.35)' : 'rgba(255,255,255,0.08)'}`,
          fontSize: 11,
          color: bp.isActive ? '#5cbf63' : '#6b7280',
        }}
      >
        {rank}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span
            className="text-white font-semibold truncate"
            style={{ fontSize: 13, fontFamily: 'JetBrains Mono, monospace' }}
          >
            {bp.account}
          </span>
          {isCurrentVote && (
            <span
              style={{
                fontSize: 9, color: '#5cbf63',
                background: 'rgba(67,160,71,0.15)',
                padding: '1px 5px', borderRadius: 4, flexShrink: 0,
              }}
            >
              ✓ voted
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span style={{ fontSize: 11, color: '#6b7280' }}>{bp.totalVotes} votes</span>
          {flag !== '🌐' && <span style={{ fontSize: 12 }}>{flag}</span>}
          {bp.url && (
            <a
              href={bp.url.startsWith('http') ? bp.url : `https://${bp.url}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{ fontSize: 11, color: 'rgba(167,139,250,0.6)' }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
            </a>
          )}
        </div>
      </div>

      {/* Status or checkbox */}
      {selecting ? (
        <div
          className="flex-shrink-0 flex items-center justify-center"
          style={{
            width: 20, height: 20, borderRadius: 6,
            background: checked ? '#43a047' : 'transparent',
            border: `1.5px solid ${checked ? '#43a047' : '#4b5563'}`,
            transition: 'all 0.15s',
          }}
        >
          {checked && (
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20,6 9,17 4,12"/>
            </svg>
          )}
        </div>
      ) : (
        <span className={bp.isActive ? 'badge-active' : 'badge-standby'}>
          {bp.isActive ? '● Active' : '○ Standby'}
        </span>
      )}
    </button>
  )
}

// ── Main Screen ────────────────────────────────────────────────────────────────

export default function GovernanceScreen() {
  const { t } = useTranslation()
  const { activeAccount, activeSession, isSessionLocked, goBack } = useWalletStore()

  const [producers, setProducers]     = useState<BlockProducer[]>([])
  const [voterInfo, setVoterInfo]     = useState<VoteInfo | null>(null)
  const [loading, setLoading]         = useState(true)
  const [activeTab, setActiveTab]     = useState<Tab>('top21')
  const [selecting, setSelecting]     = useState(false)
  const [selectedBps, setSelectedBps] = useState<Set<string>>(new Set())
  const [search, setSearch]           = useState('')
  const [voting, setVoting]           = useState(false)
  const [voteResult, setVoteResult]   = useState<{ ok: boolean; msg: string } | null>(null)
  const [showConfirm, setShowConfirm] = useState(false)

  // ── All data loading logic UNCHANGED ──────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [bps, voter] = await Promise.all([
        getTopProducers(100),
        activeAccount ? getVoterInfo(activeAccount.name) : null,
      ])
      setProducers(bps)
      setVoterInfo(voter)
    } catch (e) {
      console.error('[governance] load error:', e)
    } finally {
      setLoading(false)
    }
  }, [activeAccount])

  useEffect(() => { void loadData() }, [loadData])

  useEffect(() => {
    if (!voteResult) return
    const timer = setTimeout(() => setVoteResult(null), 3000)
    return () => clearTimeout(timer)
  }, [voteResult])

  const top21   = useMemo(() => producers.slice(0, 21), [producers])
  const standby = useMemo(() => producers.slice(21), [producers])
  const currentVotes = useMemo(() => new Set(voterInfo?.producers ?? []), [voterInfo])

  const filteredList = useMemo(() => {
    const list = activeTab === 'top21' ? top21 : activeTab === 'standby' ? standby : producers
    if (!search.trim()) return list
    const q = search.toLowerCase()
    return list.filter(bp => bp.account.toLowerCase().includes(q))
  }, [activeTab, top21, standby, producers, search])

  const enterSelectMode = useCallback(() => {
    setSelectedBps(new Set(voterInfo?.producers ?? []))
    setSelecting(true)
    if (activeTab === 'myvote') setActiveTab('top21')
  }, [voterInfo, activeTab])

  const toggleBp = useCallback((account: string) => {
    setSelectedBps(prev => {
      const next = new Set(prev)
      if (next.has(account)) { next.delete(account) }
      else { if (next.size >= 30) return prev; next.add(account) }
      return next
    })
  }, [])

  const cancelSelect = useCallback(() => {
    setSelecting(false)
    setSelectedBps(new Set())
    setSearch('')
  }, [])

  const submitVote = useCallback(async () => {
    if (!activeSession || !activeAccount) return
    setShowConfirm(false)
    setVoting(true)
    try {
      const txId = await voteForProducers(activeSession, activeAccount.name, Array.from(selectedBps))
      setVoteResult({ ok: true, msg: `${t('governance.voteSuccess')} · ${txId.slice(0, 8)}…` })
      setSelecting(false)
      setSelectedBps(new Set())
      if (activeAccount) {
        const newVoter = await getVoterInfo(activeAccount.name)
        setVoterInfo(newVoter)
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setVoteResult({ ok: false, msg: msg.slice(0, 80) })
    } finally {
      setVoting(false) }
  }, [activeSession, activeAccount, selectedBps, t])

  const selectedCount = selectedBps.size
  const hasChanges = selecting && (
    selectedCount !== currentVotes.size ||
    [...selectedBps].some(b => !currentVotes.has(b))
  )

  // ── Confirm dialog ─────────────────────────────────────────────
  if (showConfirm) {
    const sorted = [...selectedBps].sort()
    return (
      <div className="flex flex-col h-full" style={{ background: '#000000' }}>
        <div
          className="pt-safe px-4 py-4 flex-shrink-0"
          style={{ background: '#0c100c', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          <h2 className="text-white font-bold" style={{ fontSize: 18 }}>{t('governance.confirmVote')}</h2>
          <p className="mt-1" style={{ fontSize: 12, color: '#6b7280' }}>
            {selectedCount} {t('governance.producers')} · {t('governance.maxBps')}
          </p>
        </div>

        <div className="scroll-area flex-1 px-4 py-3 space-y-1.5">
          {sorted.length === 0 ? (
            <p className="text-sm text-center py-4" style={{ color: '#6b7280' }}>{t('governance.clearVotes')}</p>
          ) : (
            sorted.map((name, i) => {
              const bp = producers.find(p => p.account === name)
              return (
                <div
                  key={name}
                  className="flex items-center gap-3 py-2"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                >
                  <span style={{ color: '#6b7280', fontSize: 12, width: 24, textAlign: 'right' }}>{i + 1}</span>
                  <span className="text-white font-medium flex-1" style={{ fontSize: 13, fontFamily: 'JetBrains Mono, monospace' }}>{name}</span>
                  {bp && <span className={bp.isActive ? 'badge-active' : 'badge-standby'}>{bp.isActive ? t('governance.activeLabel') : t('governance.standbyLabel')}</span>}
                </div>
              )
            })
          )}
        </div>

        <div
          className="px-4 py-3 flex gap-3 flex-shrink-0"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
        >
          <button className="btn-secondary flex-1" style={{ height: 48 }} onClick={() => setShowConfirm(false)}>
            {t('common.cancel')}
          </button>
          <button className="btn-primary flex-1" style={{ height: 48 }} onClick={submitVote} disabled={voting}>
            {voting ? t('governance.voting') : t('governance.confirmVote')}
          </button>
        </div>
      </div>
    )
  }

  // ── Main layout ────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full" style={{ background: '#000000' }}>

      {/* ── Header ─────────────────────────────────────────────── */}
      <div
        className="pt-safe px-4 pb-4 flex-shrink-0"
        style={{ background: '#0c100c', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={goBack}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#9ca3af', fontSize: 13 }}
            >
              ← {t('tools.title')}
            </button>
            <AccountAvatar avatarUrl={activeAccount?.avatarUrl} size="xs" />
            <h2 className="text-white font-bold" style={{ fontSize: 20 }}>{t('governance.title')}</h2>
          </div>
          <button
            onClick={loadData}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: '#9ca3af' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              {loading
                ? <circle cx="12" cy="12" r="9" style={{ strokeDasharray: '28 56' }} className="animate-spin"/>
                : <><polyline points="23,4 23,10 17,10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></>
              }
            </svg>
          </button>
        </div>

        {/* Voter banner */}
        {voterInfo && !selecting && (
          <div className="banner-brand flex items-center gap-2.5 mb-4">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#5cbf63" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <div className="flex-1 min-w-0">
              {voterInfo.proxy ? (
                <p style={{ fontSize: 12, color: '#5cbf63' }}>
                  🔄 {t('governance.proxyVoting')}: <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{voterInfo.proxy}</span>
                </p>
              ) : voterInfo.producers.length > 0 ? (
                <>
                  <p style={{ fontSize: 12, color: '#5cbf63', fontWeight: 600 }}>
                    Voting for {voterInfo.producers.length} producers
                  </p>
                  {voterInfo.staked > 0 && (
                    <p style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                      {voterInfo.stakedEos} EOS staked
                    </p>
                  )}
                </>
              ) : (
                <p style={{ fontSize: 12, color: '#6b7280' }}>{t('governance.noVotes')}</p>
              )}
            </div>
            {!isSessionLocked && (
              <button onClick={enterSelectMode} className="btn-ghost" style={{ padding: '4px 10px', fontSize: 11, height: 28 }}>
                {t('governance.changeVotes')}
              </button>
            )}
          </div>
        )}

        {/* Selection search or normal tabs */}
        {selecting ? (
          <>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('governance.searchBps')}
                className="input-field flex-1"
                style={{ height: 40, fontSize: 13 }}
              />
              <button
                onClick={cancelSelect}
                style={{ color: '#9ca3af', fontSize: 12, background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', whiteSpace: 'nowrap' }}
              >
                {t('governance.cancelSelect')}
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                {(['top21', 'standby'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`tab-pill${activeTab === tab ? ' active' : ''}`}
                    style={{ padding: '5px 12px', fontSize: 11 }}
                  >
                    {tab === 'top21' ? 'Top 21' : 'Standby'}
                  </button>
                ))}
              </div>
              <span style={{ fontSize: 11, color: selectedCount >= 30 ? '#f59e0b' : '#6b7280' }}>
                {selectedCount}/30 {t('governance.selected')}
              </span>
            </div>
          </>
        ) : (
          <div className="flex gap-2">
            {(['top21', 'standby', 'myvote'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`tab-pill${activeTab === tab ? ' active' : ''}`}
              >
                {tab === 'top21'
                  ? `${t('governance.top21')} (21)`
                  : tab === 'standby'
                    ? `${t('governance.standby')} (${standby.length})`
                    : t('governance.myVotes')}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Toast */}
      {voteResult && (
        <div
          className="mx-4 mt-2 px-3 py-2 rounded-xl text-sm flex-shrink-0 animate-fade-in"
          style={{
            background: voteResult.ok ? 'rgba(74,222,128,0.12)' : 'rgba(239,68,68,0.12)',
            border: `1px solid ${voteResult.ok ? 'rgba(74,222,128,0.25)' : 'rgba(239,68,68,0.25)'}`,
            color: voteResult.ok ? '#4ade80' : '#ef4444',
          }}
        >
          {voteResult.ok ? '✓ ' : '✗ '}{voteResult.msg}
        </div>
      )}

      {/* ── Content ────────────────────────────────────────────── */}
      <div className="scroll-area flex-1 px-4 py-3">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div
              className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: '#43a047', borderTopColor: 'transparent' }}
            />
          </div>
        ) : activeTab === 'myvote' && !selecting ? (
          <MyVotesTab
            voterInfo={voterInfo}
            producers={producers}
            top21={top21}
            isSessionLocked={isSessionLocked}
            onStartVoting={enterSelectMode}
            t={t}
          />
        ) : activeTab === 'standby' && !selecting ? (
          <>
            {/* Standby info card */}
            <div
              className="mb-4"
              style={{
                background: 'rgba(167,139,250,0.05)',
                border: '1px solid rgba(167,139,250,0.15)',
                borderRadius: 14,
                padding: '14px 16px',
              }}
            >
              <div className="flex gap-2.5 items-start">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#5cbf63" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#5cbf63', marginBottom: 4 }}>
                    {t('governance.standbyTitle')}
                  </p>
                  <p style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.6 }}>
                    {t('governance.standbyExplain')}
                  </p>
                </div>
              </div>
            </div>
            {filteredList.map(bp => (
              <BpCard key={bp.account} bp={bp} rank={bp.rank} checked={selectedBps.has(bp.account)} selecting={selecting} isCurrentVote={currentVotes.has(bp.account)} onToggle={toggleBp} />
            ))}
          </>
        ) : (
          filteredList.length === 0
            ? <p className="text-center text-sm py-8" style={{ color: '#6b7280' }}>{t('common.search')}…</p>
            : filteredList.map(bp => (
                <BpCard key={bp.account} bp={bp} rank={bp.rank} checked={selectedBps.has(bp.account)} selecting={selecting} isCurrentVote={currentVotes.has(bp.account)} onToggle={toggleBp} />
              ))
        )}
        <div style={{ height: selecting ? 80 : 16 }} />
      </div>

      {/* Floating vote button */}
      {selecting && (
        <div
          className="px-4 pt-2 flex-shrink-0"
          style={{
            borderTop: '1px solid rgba(255,255,255,0.06)',
            background: '#000000',
            paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
          }}
        >
          {isSessionLocked ? (
            <p className="text-center text-xs py-1" style={{ color: '#6b7280' }}>{t('governance.walletLocked')}</p>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedBps(new Set())}
                className="btn-secondary"
                style={{ height: 48, padding: '0 14px', flexShrink: 0 }}
                title={t('governance.clearVotes')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
              <button
                disabled={!hasChanges || voting}
                onClick={() => setShowConfirm(true)}
                className="btn-primary flex-1"
                style={{ height: 48, opacity: (!hasChanges || voting) ? 0.4 : 1 }}
              >
                {voting
                  ? t('governance.voting')
                  : `🗳️ ${t('governance.voteFor')} (${selectedCount})`}
              </button>
            </div>
          )}
        </div>
      )}

      {!selecting && <BottomNav />}
    </div>
  )
}

// ── My Votes Tab ───────────────────────────────────────────────────────────────

interface MyVotesTabProps {
  voterInfo: VoteInfo | null
  producers: BlockProducer[]
  top21: BlockProducer[]
  isSessionLocked: boolean
  onStartVoting: () => void
  t: (key: TranslationKey) => string
}

function MyVotesTab({ voterInfo, producers, top21, isSessionLocked, onStartVoting, t }: MyVotesTabProps) {
  const active21Names = new Set(top21.map(b => b.account))

  if (!voterInfo || (voterInfo.producers.length === 0 && !voterInfo.proxy)) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 12l2 2 4-4"/>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        <div className="text-center">
          <p className="font-medium" style={{ color: '#9ca3af' }}>{t('governance.noVotes')}</p>
          <p className="text-xs mt-1 px-8" style={{ color: '#6b7280', lineHeight: 1.6 }}>
            {t('governance.reVoteHint')}
          </p>
        </div>
        {!isSessionLocked && (
          <button onClick={onStartVoting} className="btn-primary" style={{ padding: '10px 24px', fontSize: 13 }}>
            {t('governance.selectBps')}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Stats 2×2 grid */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: 'Producers', value: voterInfo.producers.length },
          { label: t('governance.stakedAmount'), value: voterInfo.staked > 0 ? `${voterInfo.stakedEos} EOS` : '—' },
          { label: 'Vote weight', value: '—' },
          { label: 'Last voted', value: '—' },
        ].map((stat, i) => (
          <div
            key={i}
            style={{
              background: '#0c100c',
              border: '1px solid rgba(255,255,255,0.05)',
              borderRadius: 14, padding: '14px 14px',
            }}
          >
            <p style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>{stat.label}</p>
            <p className="text-white font-bold" style={{ fontSize: 20 }}>{stat.value}</p>
          </div>
        ))}
      </div>

      {voterInfo.proxy && (
        <div style={{ background: '#141914', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '10px 14px' }}>
          <p style={{ fontSize: 11, color: '#6b7280' }}>{t('governance.proxyVoting')}</p>
          <p className="text-white" style={{ fontSize: 13, fontFamily: 'JetBrains Mono, monospace', marginTop: 2 }}>{voterInfo.proxy}</p>
        </div>
      )}

      {/* Change Votes */}
      {!isSessionLocked && (
        <button onClick={onStartVoting} className="btn-primary w-full" style={{ height: 48 }}>
          {t('governance.changeVotes')}
        </button>
      )}

      {/* Voted producers list */}
      {voterInfo.producers.length > 0 && (
        <>
          <p className="section-label">{t('governance.myVotes')}</p>
          {voterInfo.producers.map((name, i) => {
            const bp = producers.find(p => p.account === name)
            const isActive = active21Names.has(name)
            return (
              <div
                key={name}
                className="flex items-center gap-3"
                style={{
                  background: '#0c100c',
                  border: '1px solid rgba(255,255,255,0.05)',
                  borderRadius: 12, padding: '10px 14px', marginBottom: 6,
                }}
              >
                <span style={{ color: '#4b5563', fontSize: 11, width: 20, textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium" style={{ fontSize: 13, fontFamily: 'JetBrains Mono, monospace' }}>{name}</p>
                  {bp && (
                    <p style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
                      {bp.totalVotes} votes{bp.countryCode ? ` · ${countryFlag(bp.countryCode)}` : ''}
                    </p>
                  )}
                </div>
                <span className={isActive ? 'badge-active' : 'badge-standby'} style={{ flexShrink: 0 }}>
                  {isActive ? `● ${t('governance.activeLabel')}` : `○ ${t('governance.standbyLabel')}`}
                </span>
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
